import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BREAKPOINT_FOR_RIGHT_RAIL_COVER,
  useLayoutContext,
} from '../BaseLayout/context/LayoutContext';
import useBreakpointStatus from '../BaseLayout/hooks/useBreakpointStatus';

/**
 * These breakpoints conform to the standard set by design.
 */
enum GridBreakpoints {
  xs = 600,
  sm = 960,
  md = 1280,
  lg = 1440,
  xl = 1720,
  xxl = 1920,
}

const GA_CONTENT = 'content';

// string used in the grid template areas to create a single column layout
const getRepeatedString = (numReps: number, str?: string) =>
  Array.from({ length: numReps }, () => str || GA_CONTENT).join(' ');

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
  container: {
    display: 'grid',
    width: '100%',
    gap: '24px',
    maxWidth: '480px',
    gridTemplateAreas: `". ${getRepeatedString(6)} ."`,
    gridTemplateColumns: `minmax(1vw, auto) repeat(6, [col-start] 1fr) 1vw`,
    transition: theme.transitions.create(['max-width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.up(GridBreakpoints.sm)]: {
      gridTemplateAreas: `". ${getRepeatedString(12)} ."`,
      gridTemplateColumns: `minmax(1vw, auto) repeat(12, [col-start] 1fr) 1vw`,
    },
  },
  item: {
    gridArea: GA_CONTENT,
  },
  narrow: {
    [theme.breakpoints.up(GridBreakpoints.sm)]: {
      maxWidth: theme.spacing(60), // 480px
    },
    [theme.breakpoints.up(GridBreakpoints.md)]: {
      maxWidth: theme.spacing(77), // 616px
    },
    [theme.breakpoints.up(GridBreakpoints.lg)]: {
      maxWidth: theme.spacing(95), // 760px
    },
  },
  medium: {
    [theme.breakpoints.up(GridBreakpoints.sm)]: {
      maxWidth: theme.spacing(95), // 760px
    },
    [theme.breakpoints.up(GridBreakpoints.md)]: {
      maxWidth: theme.spacing(107), // 856px
    },
    [theme.breakpoints.up(GridBreakpoints.lg)]: {
      maxWidth: theme.spacing(128), // 1024px
    },
  },
  wide: {
    [theme.breakpoints.up(GridBreakpoints.xs)]: {
      maxWidth: theme.spacing(97), // 774px
    },
    [theme.breakpoints.up(GridBreakpoints.sm)]: {
      maxWidth: theme.spacing(131), // 1048px
    },
    [theme.breakpoints.up(GridBreakpoints.md)]: {
      maxWidth: theme.spacing(152), // 1216px
    },
  },
}));

/**
 * @description Layout component, intended to be used to wrap a page's content, that centers the
 * content and adjusts the width based on the state of the left and right rails.
 *
 * @see https://www.figma.com/design/TWgty8gcTip6JWbfKJigFb/WSL-Breve-Component-Library?m=dev&node-id=2604-20641
 */
const Centered = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string; style?: React.CSSProperties }
>(({ children, className, style }, ref) => {
  const { classes, cx } = useStyles();
  const { isRightRailOpen, isLeftRailOpen } = useLayoutContext();
  const bpStatus = useBreakpointStatus({
    breakpointToCover: BREAKPOINT_FOR_RIGHT_RAIL_COVER,
  });

  /**
   * This determines the size of the grid based on the state of the rails.
   * If the breakpoint is at or below the breakpoint for the right rail cover,
   * we should not use narrow sizes.
   */
  const sizeClassName = useMemo(() => {
    if (bpStatus?.isAtBreakpoint || bpStatus?.isBelowBreakpoint) {
      return isLeftRailOpen ? classes.medium : classes.wide;
    }

    if (isLeftRailOpen && isRightRailOpen) {
      return classes.narrow;
    }
    if (isLeftRailOpen === isRightRailOpen && !isLeftRailOpen) {
      return classes.wide;
    }
    return classes.medium;
  }, [classes, isLeftRailOpen, isRightRailOpen, bpStatus]);

  return (
    <div className={cx(classes.root, className)} style={style} ref={ref}>
      <Grid container className={cx(classes.container, sizeClassName)}>
        <div className={classes.item}>{children}</div>
      </Grid>
    </div>
  );
});

export default Centered;
